import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Button, Modal, Typography, Space, Form, Row, Col, Checkbox,
  InputNumber, App as AntApp, Flex, Select, Tag, Alert, Card, Upload,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, MergeCellsOutlined,
  UploadOutlined, EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import BuscaCnpjBox from './BuscaCnpjBox';
import ContatosVinculadosBox, { ContatosVinculadosHandle } from '../components/ContatosVinculadosBox';
import { usePerms } from '../hooks/usePerms';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Cliente {
  id: number;
  razao_social: string;
  nome_fantasia: string | null;
  cnpj: string | null;
  segmento: string | null;
  uf: string | null;
  cidade: string | null;
  contato_nome: string | null;
  contato_email: string | null;
  contato_fone: string | null;
  observacoes: string | null;
  ativo: boolean;
}

function ComercialClientesInner() {
  const { message, modal } = AntApp.useApp();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [showMesclar, setShowMesclar] = useState(false);
  // O servidor recusa criar, editar, mesclar e excluir empresa sem a permissão
  // (05/10/2026); a tela esconde o que seria recusado e abre o cadastro só para
  // leitura. Falha ao carregar a matriz não vira "não pode" — o servidor
  // continua sendo a trava.
  const { pode, erro: permsErro } = usePerms();
  const podeCriar = permsErro || pode('comercial.empresas.criar');
  const podeEditar = permsErro || pode('comercial.empresas.editar');
  const podeExcluir = permsErro || pode('comercial.empresas.excluir');
  // Trocar "retém ISS" de empresa existente recalcula o A Receber: é do financeiro.
  const podeRetencao = permsErro || pode('financeiro.contas_receber.editar');

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/clientes`, {
        headers: { Authorization: token },
      });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setClientes(data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function aoSalvar() {
    setShowForm(false);
    setEditando(null);
    await carregar();
  }

  async function excluirCliente(c: Cliente) {
    modal.confirm({
      title: `Excluir cliente "${c.razao_social}"?`,
      content: 'Se houver propostas, contratos, ARTs ou contas a receber vinculadas, a exclusão será bloqueada. Use "Mesclar" pra consolidar com outro cliente antes.',
      okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/comercial/clientes/${c.id}`,
            { headers: { Authorization: token } });
          message.success('Cliente excluído.');
          await carregar();
        } catch (e: any) {
          const erroMsg = e.response?.data?.erro || e.response?.data?.error || e.message;
          if (e.response?.status === 409 || /foreign key|referenc/i.test(erroMsg)) {
            message.error('Não foi possível excluir: cliente tem vínculos. Use "Mesclar" pra consolidar com outro.');
          } else {
            message.error('Erro: ' + erroMsg);
          }
        }
      },
    });
  }

  const filtrados = clientes.filter(c => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (c.razao_social?.toLowerCase().includes(q) ||
      c.nome_fantasia?.toLowerCase().includes(q) ||
      c.cnpj?.includes(q) ||
      c.segmento?.toLowerCase().includes(q));
  });

  const columns: ColumnsType<Cliente> = [
    {
      title: 'Razão Social', dataIndex: 'razao_social', key: 'rs',
      render: (v) => <Text strong style={{ color: '#020D7F' }}>{v}</Text>,
    },
    { title: 'Nome Fantasia', dataIndex: 'nome_fantasia', key: 'nf', render: (v) => v || '—' },
    {
      title: 'CNPJ', dataIndex: 'cnpj', key: 'cnpj', width: 160,
      render: (v) => v ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> : '—',
    },
    { title: 'Segmento', dataIndex: 'segmento', key: 'seg', render: (v) => v || '—' },
    { title: 'UF', dataIndex: 'uf', key: 'uf', width: 50, align: 'center', render: (v) => v || '—' },
    {
      title: 'Contato', key: 'contato',
      render: (_, r) => (
        <div style={{ fontSize: 11 }}>
          {r.contato_nome && <div>{r.contato_nome}</div>}
          {r.contato_email && <Text style={{ color: '#0930D0' }}>{r.contato_email}</Text>}
          {!r.contato_nome && !r.contato_email && '—'}
        </div>
      ),
    },
    {
      title: '', key: 'acoes', width: 90, align: 'center',
      render: (_, r) => (
        <Space size={2}>
          <Button size="small" type="text" icon={podeEditar ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => { setEditando(r); setShowForm(true); }} title={podeEditar ? 'Editar' : 'Ver'} />
          {podeExcluir && (
            <Button size="small" type="text" danger icon={<DeleteOutlined />}
              onClick={() => excluirCliente(r)} title="Excluir" />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <Flex gap={12} align="center" style={{ marginBottom: 16 }} wrap="wrap">
        <Input
          placeholder="Buscar por razão social, fantasia, CNPJ ou segmento..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ maxWidth: 400, flex: 1 }}
        />
        {podeExcluir && selecionados.length >= 2 && (
          <Button danger icon={<MergeCellsOutlined />}
            onClick={() => setShowMesclar(true)}>
            Mesclar ({selecionados.length})
          </Button>
        )}
        {selecionados.length > 0 && (
          <Button onClick={() => setSelecionados([])}>
            Limpar seleção
          </Button>
        )}
        {podeCriar && (
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => { setEditando(null); setShowForm(true); }}
            style={{ marginLeft: selecionados.length > 0 ? 0 : 'auto' }}>
            Novo Cliente
          </Button>
        )}
      </Flex>

      {selecionados.length >= 2 && (
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
          message={`${selecionados.length} clientes selecionados`}
          description="Clique em 'Mesclar' pra escolher qual manter — os outros serão consolidados (propostas, contratos, contas a receber, contatos migrados pro mantido)." />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtrados}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        // Selecionar serve só para mesclar, que apaga os duplicados.
        rowSelection={podeExcluir ? {
          selectedRowKeys: selecionados,
          onChange: (keys) => setSelecionados(keys as number[]),
        } : undefined}
        locale={{ emptyText: clientes.length === 0 ? 'Nenhum cliente cadastrado. Crie o primeiro!' : 'Nenhum resultado.' }}
        scroll={{ x: 'max-content' }}
      />

      {showForm && (
        <FormCliente
          cliente={editando}
          somenteLeitura={editando ? !podeEditar : !podeCriar}
          podeRetencao={podeRetencao}
          onClose={() => { setShowForm(false); setEditando(null); }}
          onSaved={aoSalvar}
        />
      )}

      {showMesclar && (
        <ModalMesclarClientes
          clientes={clientes.filter(c => selecionados.includes(c.id))}
          onFechar={() => setShowMesclar(false)}
          onSalvou={() => {
            setShowMesclar(false);
            setSelecionados([]);
            carregar();
          }} />
      )}
    </div>
  );
}

// Modal de mesclar clientes
function ModalMesclarClientes({ clientes, onFechar, onSalvou }: {
  clientes: Cliente[]; onFechar: () => void; onSalvou: () => void;
}) {
  const { message } = AntApp.useApp();
  const [manterId, setManterId] = useState<number | null>(
    // Pré-seleciona o que tem CNPJ (provavelmente o mais completo)
    clientes.find(c => c.cnpj)?.id || clientes[0]?.id || null
  );
  const [salvando, setSalvando] = useState(false);

  async function mesclar() {
    if (!manterId) { message.warning('Selecione qual cliente manter'); return; }
    const excluirIds = clientes.filter(c => c.id !== manterId).map(c => c.id);
    if (excluirIds.length === 0) { message.warning('Selecione pelo menos 2 clientes'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/clientes/mesclar`,
        { manter_id: manterId, excluir_ids: excluirIds },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(
        `Mesclado: ${d.removidos} cliente(s) consolidado(s). ` +
        `Propostas: ${d.propostas_movidas || 0}, contratos: ${d.contratos_movidos || 0}, ` +
        `CR: ${d.contas_a_receber_movidas || 0}.`
      );
      onSalvou();
    } catch (e: any) {
      message.error('Erro ao mesclar: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onFechar} onOk={mesclar} confirmLoading={salvando}
      title={`Mesclar ${clientes.length} clientes`} okText="Mesclar" okType="danger" cancelText="Cancelar"
      width={600} destroyOnClose>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }}
        message="Operação irreversível"
        description="Os clientes não selecionados serão DELETADOS. Todas as referências (propostas, contratos, ARTs, contas a receber, contatos) são migradas pro cliente mantido." />
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Qual cliente MANTER?</Text>
      <Space direction="vertical" style={{ width: '100%' }}>
        {clientes.map(c => (
          <Card key={c.id} size="small"
            style={{
              cursor: 'pointer',
              borderColor: manterId === c.id ? '#0930D0' : undefined,
              borderWidth: manterId === c.id ? 2 : 1,
              background: manterId === c.id ? '#F0F4FF' : undefined,
            }}
            onClick={() => setManterId(c.id)}>
            <Flex justify="space-between" align="center">
              <div>
                <Text strong>{c.razao_social}</Text>
                {c.nome_fantasia && <Text type="secondary"> · {c.nome_fantasia}</Text>}
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  ID: {c.id}
                  {c.cnpj ? <> · CNPJ: <strong>{c.cnpj}</strong></> : <> · <Tag color="warning" style={{ fontSize: 10 }}>sem CNPJ</Tag></>}
                  {c.uf && <> · {c.uf}</>}
                </div>
              </div>
              {manterId === c.id && <Tag color="success">✓ Manter</Tag>}
              {manterId !== c.id && <Tag color="error">✗ Excluir</Tag>}
            </Flex>
          </Card>
        ))}
      </Space>
    </Modal>
  );
}


function FormCliente({ cliente, somenteLeitura, podeRetencao, onClose, onSaved }: {
  cliente: Cliente | null;
  somenteLeitura: boolean;
  podeRetencao: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [form, setForm] = useState<any>({
    razao_social: cliente?.razao_social || '',
    nome_fantasia: cliente?.nome_fantasia || '',
    cnpj: cliente?.cnpj || '',
    segmento: cliente?.segmento || '',
    porte: (cliente as any)?.porte || '',
    cnae_codigo: (cliente as any)?.cnae_codigo || '',
    cnae_descricao: (cliente as any)?.cnae_descricao || '',
    endereco: (cliente as any)?.endereco || '',
    bairro: (cliente as any)?.bairro || '',
    cep: (cliente as any)?.cep || '',
    uf: cliente?.uf || '',
    cidade: cliente?.cidade || '',
    situacao_rfb: (cliente as any)?.situacao_rfb || '',
    data_abertura: (cliente as any)?.data_abertura || null,
    observacoes: cliente?.observacoes || '',
    // Cliente NOVO nasce "não retém" (Lucas, 21/09/2026). A regra era o
    // inverso (`=== false ? false : true`): todo cliente criado por esta tela
    // saía retentor sem ninguém marcar — foi assim com a LOG III em 16/09, e o
    // líquido da parcela caiu 2% até alguém desmarcar. O banco e os outros 7
    // caminhos que criam cliente já gravam false; só esta linha divergia.
    retentor_iss: (cliente as any)?.retentor_iss === true,
    iss_aliquota: (cliente as any)?.iss_aliquota ?? 0.02,  // ISS da Hydrodata é 2% (POA)
    logo_s3_key: (cliente as any)?.logo_s3_key || null,  // ATM-T1
  });
  const [salvando, setSalvando] = useState(false);
  const contatosRef = useRef<ContatosVinculadosHandle>(null);

  const set = (k: string, v: any) => setForm((prev: any) => ({ ...prev, [k]: v }));

  function aplicarDadosRfb(dados: any) {
    setForm((f: any) => ({
      ...f,
      ...dados,
      razao_social: f.razao_social || dados.razao_social,
      segmento: f.segmento || dados.cnae_descricao,
      observacoes: f.observacoes,
      retentor_iss: f.retentor_iss,
      rfb_atualizado_em: new Date().toISOString(),
    }));
  }

  // Mudou "retém ISS": o servidor recalculou as parcelas ainda sem NF e conta
  // o que fez — e o que NÃO fez, para alguém conferir.
  function mostrarRetencao(retencao: any) {
    const n = retencao.recalculadas?.length || 0;
    const comNf = retencao.com_nf_divergente || [];
    const semMexer = retencao.nao_mexidas || [];
    modal.info({
      title: `Retenção de ISS ${retencao.retentor_iss ? 'ligada' : 'desligada'} para este cliente`,
      width: 560,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: '6px 0' }}>
            {n
              ? <><strong>{n} parcela(s) ainda sem nota</strong> tiveram o líquido previsto recalculado agora.</>
              : 'Nenhuma parcela ainda sem nota precisou mudar.'}
          </p>
          {semMexer.length > 0 && (
            <p style={{ margin: '6px 0', color: '#7A5500' }}>
              {semMexer.length} ficaram como estavam porque o líquido foi ajustado à mão
              ou está em branco: {semMexer.slice(0, 5).map((p: any) => p.titulo || p.id).join('; ')}
              {semMexer.length > 5 ? '…' : ''}.
            </p>
          )}
          {comNf.length > 0 && (
            <p style={{ margin: '6px 0', color: '#B91626' }}>
              ⚠ {comNf.length} com nota já emitida {retencao.retentor_iss ? 'sem' : 'com'} retenção
              de ISS — o sistema não mexe em nota emitida. Confira:{' '}
              {comNf.slice(0, 5).map((p: any) => `NF ${p.nf || '?'} (${p.titulo || p.id})`).join('; ')}
              {comNf.length > 5 ? '…' : ''}.
            </p>
          )}
        </div>
      ),
    });
  }

  async function handleSave() {
    if (somenteLeitura) return;
    if (!form.razao_social.trim()) { message.warning('Razão social é obrigatória'); return; }
    setSalvando(true);
    let cadastroSalvo = false;
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      let clienteId: number;
      if (cliente?.id) {
        const r = await axios.put(`${API_URL}/comercial/clientes/${cliente.id}`, form, { headers });
        const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
        cadastroSalvo = true;
        // Mostrado JÁ, antes dos contatos: se eles falharem em seguida, o
        // cadastro e as parcelas já mudaram e a lista de notas para conferir
        // não pode sumir (revisão adversarial de 21/09).
        if (data?._retencao) mostrarRetencao(data._retencao);
        clienteId = cliente.id;
      } else {
        const r = await axios.post(`${API_URL}/comercial/clientes`, form, { headers });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        cadastroSalvo = true;
        clienteId = data.id;
      }
      if (contatosRef.current && clienteId) {
        await contatosRef.current.commit(clienteId);
      }
      message.success(cliente ? 'Cliente atualizado.' : 'Cliente criado.');
      onSaved();
    } catch (e: any) {
      // O backend responde { error: ... } (helper `err` da layer). Ler só
      // `message` fazia todo erro virar "Request failed with status code 500",
      // escondendo, por exemplo, o aviso de CNPJ já cadastrado.
      const d = e?.response?.data;
      const motivo = d?.error || d?.erro || d?.message || e.message;
      message.error(cadastroSalvo
        ? 'O cadastro do cliente foi salvo, mas os contatos não: ' + motivo
        : 'Erro ao salvar: ' + motivo, 8);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={handleSave}
      okText={cliente ? 'Salvar' : 'Criar Cliente'}
      okButtonProps={somenteLeitura ? { style: { display: 'none' } } : undefined}
      cancelText={somenteLeitura ? 'Fechar' : 'Cancelar'}
      confirmLoading={salvando}
      title={somenteLeitura ? 'Cliente' : cliente ? 'Editar Cliente' : 'Novo Cliente'}
      width={580}
      destroyOnClose
    >
      {!somenteLeitura && <BuscaCnpjBox cnpjInicial={form.cnpj} onEncontrado={aplicarDadosRfb} />}

      <Form layout="vertical" size="small" disabled={somenteLeitura}>
        <Form.Item label="Razão Social" required>
          <Input value={form.razao_social} onChange={e => set('razao_social', e.target.value)} />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Nome Fantasia">
              <Input value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="CNPJ">
              <Input value={form.cnpj} onChange={e => set('cnpj', e.target.value)} placeholder="00.000.000/0000-00" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Segmento">
              <Input value={form.segmento} onChange={e => set('segmento', e.target.value)} placeholder="Ex.: Energia, Saneamento" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="UF">
              <Input value={form.uf} onChange={e => set('uf', e.target.value)} maxLength={2} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="CEP">
              <Input value={form.cep} onChange={e => set('cep', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Cidade">
              <Input value={form.cidade} onChange={e => set('cidade', e.target.value)} />
            </Form.Item>
          </Col>
        </Row>

        <ContatosVinculadosBox ref={contatosRef} clienteId={cliente?.id ?? null} />

        <Row gutter={12}>
          <Col span={16}>
            <Form.Item label="Endereço (logradouro + número)">
              <Input
                value={form.endereco}
                onChange={e => set('endereco', e.target.value)}
                placeholder="Ex.: Rua das Flores, 123"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Bairro">
              <Input value={form.bairro} onChange={e => set('bairro', e.target.value)} />
            </Form.Item>
          </Col>
        </Row>

        {(form.porte || form.situacao_rfb || form.cnae_descricao) && (
          <div style={{ fontSize: 11, color: '#666', padding: 8, background: '#FAFBFF', borderRadius: 4, marginBottom: 12 }}>
            {form.porte && <>Porte: <strong>{form.porte}</strong> · </>}
            {form.situacao_rfb && <>Situação: <strong>{form.situacao_rfb}</strong> · </>}
            {form.cnae_descricao && <>CNAE: {form.cnae_codigo} — {form.cnae_descricao}</>}
          </div>
        )}

        <Form.Item style={{ marginBottom: 6 }}>
          <Checkbox
            checked={!!form.retentor_iss}
            onChange={e => set('retentor_iss', e.target.checked)}
            // `disabled` explícito vence o <Form disabled>: repete o só-leitura.
            disabled={somenteLeitura || (!!cliente?.id && !podeRetencao)}
          >
            Cliente retém ISS na fonte
          </Checkbox>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            Marque só quando o cliente desconta o ISS da nota (ex.: CORSAN).
            Isso baixa 2% do líquido de todas as parcelas dele.
            {!!cliente?.id && !podeRetencao && (
              <> Em cliente já cadastrado, quem troca é o financeiro: a troca
                recalcula as parcelas a receber.</>
            )}
          </div>
        </Form.Item>

        <Form.Item
          label="Alíquota ISS"
          extra="Federais (IR + CSLL + PIS + COFINS = 6,15%) presumidas sempre que há NF — marque 'Não há NF' na parcela quando não houver. ISS da Hydrodata é 2% (Porto Alegre)."
        >
          <InputNumber
            value={form.iss_aliquota ?? 0}
            onChange={(v) => set('iss_aliquota', Number(v) || 0)}
            min={0} max={0.1} step={0.0001}
            disabled={somenteLeitura || !form.retentor_iss}
            formatter={(v) => v ? `${(Number(v) * 100).toFixed(2)}%` : '0%'}
            parser={(v: any) => Number(String(v).replace('%', '')) / 100}
            style={{ width: 120 }}
          />
        </Form.Item>

        <Form.Item label="Observações">
          <Input.TextArea
            value={form.observacoes}
            onChange={e => set('observacoes', e.target.value)}
            rows={2}
          />
        </Form.Item>

        {/* ATM-T1 (2026-06-07): upload do logo pra usar em atestados */}
        <Form.Item label="Logo (usado em atestados técnicos)">
          <UploadLogoCliente clienteId={cliente?.id || null}
            s3Key={form.logo_s3_key}
            onChange={(k) => set('logo_s3_key', k)} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UploadLogoCliente({ clienteId, s3Key, onChange }: {
  clienteId: number | null;
  s3Key: string | null;
  onChange: (s3Key: string | null) => void;
}) {
  const { message } = AntApp.useApp();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Carrega preview signed URL quando tem s3_key
  React.useEffect(() => {
    if (!s3Key) { setPreviewUrl(null); return; }
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/clientes`,
          { action: 'com_download_url_logo_cliente', s3_key: s3Key },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setPreviewUrl(d.url);
      } catch (e) { /* silencia */ }
    })();
  }, [s3Key]);

  async function handleUpload(file: File) {
    if (!clienteId) {
      message.warning('Salve o cliente primeiro pra subir o logo');
      return false;
    }
    if (!/\.(png|jpe?g|svg)$/i.test(file.name)) {
      message.error('Use PNG, JPG ou SVG');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('Máximo 5MB');
      return false;
    }
    setUploading(true);
    try {
      const token = await getToken();
      // 1. Pega presigned PUT
      const r = await axios.post(`${API_URL}/comercial/clientes`,
        { action: 'com_upload_url_logo_cliente',
          cliente_id: clienteId,
          filename: file.name,
          content_type: file.type || 'image/png' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      // 2. PUT direto pro S3
      await axios.put(d.upload_url, file, {
        headers: { 'Content-Type': file.type || 'image/png' },
      });
      // 3. Atualiza form (vai salvar quando user clicar Save no modal de cliente)
      onChange(d.s3_key);
      message.success('Logo enviado — clique em Salvar pra confirmar');
    } catch (e: any) {
      const d = e?.response?.data;
      message.error('Falha: ' + (d?.error || d?.erro || d?.message || e.message));
    } finally { setUploading(false); }
    return false;  // bloqueia upload automático Antd
  }

  return (
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      {previewUrl && (
        <img src={previewUrl} alt="logo"
          style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain',
            border: '1px solid #eee', borderRadius: 4, padding: 4, background: '#fff' }} />
      )}
      <Upload accept=".png,.jpg,.jpeg,.svg,image/*"
        beforeUpload={handleUpload} showUploadList={false} maxCount={1}>
        <Button icon={<UploadOutlined />} loading={uploading}>
          {s3Key ? 'Trocar logo' : 'Subir logo'}
        </Button>
      </Upload>
      {s3Key && (
        <Button size="small" type="link" danger onClick={() => onChange(null)}>
          Remover logo
        </Button>
      )}
    </Space>
  );
}

export default function ComercialClientes() {
  return (
    <AntApp>
      <ComercialClientesInner />
    </AntApp>
  );
}
